import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import QuickActionPanel from '../../components/ui/QuickActionPanel';

import WelcomePanel from './components/WelcomePanel';
import UpcomingClasses from './components/UpcomingClasses';
import WorkoutSummary from './components/WorkoutSummary';
import AIRecommendations from './components/AIRecommendations';
import QuickActions from './components/QuickActions';
import CommunitySection from './components/CommunitySection';
import FitnessStats from './components/FitnessStats';
import WearableSync from './components/WearableSync';

const MemberDashboard = () => {
  const navigate = useNavigate();
  const [memberData, setMemberData] = useState(null);

  useEffect(() => {
    const mockMemberData = {
      firstName: "Sarah",
      lastName: "Johnson",
      photo: "https://img.rocket.new/generatedImages/rocket_gen_img_159d2c8f8-1763294679735.png",
      photoAlt: "Professional headshot of young woman with long brown hair wearing athletic wear and warm smile",
      memberId: "GM2026-1547",
      membershipType: "Premium Annual",
      membershipExpiry: "2026-12-31",
      memberSince: "Jan 2024",
      qrCode: "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=GM2026-1547",
      qrCodeAlt: "QR code containing member identification number GM2026-1547 for gym check-in scanning"
    };

    const mockUpcomingClasses = [
    {
      id: 1,
      name: "HIIT Cardio Blast",
      trainer: "Mike Rodriguez",
      location: "Studio A",
      startTime: "2026-01-25T18:00:00",
      duration: 45,
      spotsLeft: 3,
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bc13fe17-1766760299918.png",
      imageAlt: "Group of diverse people performing high-intensity interval training exercises in modern gym studio with bright lighting"
    },
    {
      id: 2,
      name: "Yoga Flow",
      trainer: "Emma Chen",
      location: "Studio B",
      startTime: "2026-01-26T09:00:00",
      duration: 60,
      spotsLeft: 8,
      image: "https://images.unsplash.com/photo-1651077837628-52b3247550ae",
      imageAlt: "Peaceful yoga class with instructor and students in various poses on colorful mats in serene studio environment"
    }];


    const mockWorkouts = [
    {
      id: 1,
      name: "Strength Builder Pro",
      goal: "Muscle Gain",
      difficulty: "Intermediate",
      sessionsCompleted: 18,
      totalSessions: 24,
      totalMinutes: 540,
      caloriesBurned: 4320
    },
    {
      id: 2,
      name: "Cardio Endurance",
      goal: "Fat Loss",
      difficulty: "Advanced",
      sessionsCompleted: 12,
      totalSessions: 16,
      totalMinutes: 360,
      caloriesBurned: 3240
    }];


    const mockRecommendations = [
    {
      id: 1,
      type: "workout",
      title: "Upper Body Power",
      description: "Based on your recent progress, this program will help you break through your bench press plateau with progressive overload techniques.",
      confidence: 94,
      duration: "45 min",
      difficulty: "Intermediate",
      tags: ["Strength", "Upper Body", "Progressive"]
    },
    {
      id: 2,
      type: "recovery",
      title: "Active Recovery Day",
      description: "Your training intensity has been high. A light yoga or swimming session will optimize muscle recovery and prevent overtraining.",
      confidence: 88,
      duration: "30 min",
      difficulty: "Beginner",
      tags: ["Recovery", "Flexibility", "Low Impact"]
    },
    {
      id: 3,
      type: "class",
      title: "Spin Class Challenge",
      description: "Your cardio endurance has improved significantly. Try our advanced spin class to push your limits and burn maximum calories.",
      confidence: 91,
      duration: "50 min",
      difficulty: "Advanced",
      tags: ["Cardio", "Group", "High Intensity"]
    }];


    const mockChallenges = [
    {
      id: 1,
      name: "January Fitness Challenge",
      description: "Complete 20 workouts this month",
      currentProgress: 14,
      goal: 20,
      unit: "workouts",
      daysLeft: 6,
      participants: 247
    },
    {
      id: 2,
      name: "Cardio King",
      description: "Burn 10,000 calories through cardio",
      currentProgress: 7250,
      goal: 10000,
      unit: "calories",
      daysLeft: 12,
      participants: 189
    }];


    const mockLeaderboard = [
    {
      id: 1,
      name: "Alex Thompson",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_187b350ba-1763294180584.png",
      avatarAlt: "Athletic man with short dark hair wearing gym attire and confident expression",
      workouts: 28,
      points: 2840
    },
    {
      id: 2,
      name: "Maria Garcia",
      avatar: "https://images.unsplash.com/photo-1733077151455-113715b5a4be",
      avatarAlt: "Fit woman with tied back hair in athletic wear showing determination",
      workouts: 26,
      points: 2650
    },
    {
      id: 3,
      name: "James Wilson",
      avatar: "https://images.unsplash.com/photo-1666891987811-99b9171906d8",
      avatarAlt: "Muscular man with beard wearing tank top and friendly smile",
      workouts: 24,
      points: 2480
    },
    {
      id: 4,
      name: "Sarah Johnson",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_159d2c8f8-1763294679735.png",
      avatarAlt: "Professional headshot of young woman with long brown hair wearing athletic wear and warm smile",
      workouts: 22,
      points: 2290
    },
    {
      id: 5,
      name: "David Lee",
      avatar: "https://images.unsplash.com/photo-1585983549404-c5a4303900c5",
      avatarAlt: "Young Asian man in fitness clothing with energetic pose",
      workouts: 21,
      points: 2180
    }];


    const mockAnnouncements = [
    {
      id: 1,
      type: "event",
      title: "New Year Fitness Bootcamp",
      message: "Join us for an intensive 4-week bootcamp starting February 1st. Limited spots available. Early bird discount ends this week!",
      date: "2 days ago"
    },
    {
      id: 2,
      type: "maintenance",
      title: "Pool Maintenance Schedule",
      message: "The swimming pool will be closed for maintenance on January 28-29. All aqua classes are rescheduled to Studio C.",
      date: "5 days ago"
    },
    {
      id: 3,
      type: "update",
      title: "New Equipment Arrival",
      message: "We\'ve added 10 new treadmills and 5 rowing machines to the cardio zone. Check them out on your next visit!",
      date: "1 week ago"
    }];


    const mockFitnessStats = {
      checkIns: 18,
      checkInTrend: 12,
      activeDays: 5,
      activeDaysTrend: 8,
      avgHeartRate: 142,
      heartRateTrend: -3,
      personalRecords: 7,
      prTrend: 15,
      streakDays: 12
    };

    const mockWearableDevices = [
    {
      id: 1,
      name: "Apple Watch Series 9",
      type: "watch",
      status: "connected",
      lastSync: "2 min ago"
    },
    {
      id: 2,
      name: "iPhone 15 Pro",
      type: "phone",
      status: "connected",
      lastSync: "5 min ago"
    }];


    setMemberData({
      member: mockMemberData,
      upcomingClasses: mockUpcomingClasses,
      workouts: mockWorkouts,
      recommendations: mockRecommendations,
      challenges: mockChallenges,
      leaderboard: mockLeaderboard,
      announcements: mockAnnouncements,
      fitnessStats: mockFitnessStats,
      wearableDevices: mockWearableDevices
    });
  }, []);

  const handleQuickAction = (actionId) => {
    const actionMap = {
      'checkin': () => console.log('Check-in initiated'),
      'book-class': () => navigate('/class-booking'),
      'start-workout': () => navigate('/workout-programs')
    };

    if (actionMap?.[actionId]) {
      actionMap?.[actionId]();
    }
  };

  const handleAction = (actionId, path) => {
    if (path && path !== '#') {
      navigate(path);
    } else {
      console.log(`Action triggered: ${actionId}`);
    }
  };

  const handleCancelClass = (classId) => {
    console.log(`Cancelling class: ${classId}`);
  };

  const handleSyncDevices = async () => {
    console.log('Syncing all devices...');
    return new Promise((resolve) => setTimeout(resolve, 2000));
  };

  const handleConnectDevice = () => {
    console.log('Opening device connection dialog...');
  };

  if (!memberData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole="member" userName={`${memberData?.member?.firstName} ${memberData?.member?.lastName}`} />
      <div className="page-content-wrapper">
        <DashboardNavigation userRole="member" />
      </div>
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-6 lg:space-y-8">
            <WelcomePanel
              member={memberData?.member}
              onCheckIn={() => handleQuickAction('checkin')} />


            <UpcomingClasses
              classes={memberData?.upcomingClasses}
              onViewAll={() => navigate('/class-booking')}
              onCancelClass={handleCancelClass} />


            <WorkoutSummary
              workouts={memberData?.workouts}
              onViewPrograms={() => navigate('/workout-programs')} />


            <AIRecommendations
              recommendations={memberData?.recommendations}
              onViewDetails={(id) => console.log(`Viewing recommendation: ${id}`)} />

          </div>

          <div className="space-y-6 lg:space-y-8">
            <QuickActions onAction={handleAction} />

            <FitnessStats stats={memberData?.fitnessStats} />

            <CommunitySection
              challenges={memberData?.challenges}
              leaderboard={memberData?.leaderboard}
              announcements={memberData?.announcements} />


            <WearableSync
              devices={memberData?.wearableDevices}
              onSync={handleSyncDevices}
              onConnect={handleConnectDevice} />

          </div>
        </div>
      </main>
      <QuickActionPanel userRole="member" onAction={handleQuickAction} />
    </div>);

};

export default MemberDashboard;